/**
 * Talking to the server, and surviving not being able to.
 *
 * Members of this community are on phones on Serbian mobile networks. Losing a link somebody
 * spent four hours earning because a tunnel ate the request would be the single worst thing this
 * app could do, so every mutation is queued locally first, carries an idempotency key, and is
 * replayed until the server confirms it.
 */

import type { BlindLabel, Duel, Osa, Tally } from '../core/dvoboj';
import type { DomDoor } from '../core/dom';
import type { Mesto } from '../core/mesto';
import type { NodeProgress } from '../core/rules';
import type { Peer, PeerWork, PosadaState } from '../core/posada';
import type { RawContent } from '../core/content';
import type { ContentProblem, DiffLine } from '../core/contentChecks';

export interface PublicMember {
  id: string;
  email: string;
  name: string | null;
  path: string | null;
  look: number;
  role: 'member' | 'admin';
  status: 'active' | 'revoked';
  day: number;
  joinedAt: number;
}

export interface ServerState {
  member: PublicMember;
  progress: Record<string, NodeProgress>;
  openedIslands: string[];
  clearedIslands: string[];
  /** Comments received and comments that came back. Rides along so no card waits on a fetch. */
  posada: PosadaState;
  /**
   * Duels this member is in that are not over, and the votes in the ones being decided.
   *
   * Rides along for the same reason the crew does: SADA has to be able to hand its one card to a
   * duel with six hours left on it, and a deadline that appears only after a second round trip is
   * a deadline that appears late.
   */
  dvoboji: Duel[];
  dvobojGlasovi: Record<string, Tally>;
  /**
   * Where this member was last standing, as an offset from that island's centre. Null for anybody
   * who has never opened the map. Read exactly once per tab, into an empty slot — see `setMesto`.
   */
  mesto: Mesto | null;
  contentVersion: number;
  serverTime: number;
}

/** One duel as the battle screen and the vote screen need it. */
export interface DuelView {
  duel: Duel;
  /**
   * True while the viewer is only voting on this duel. Everything indexed by side — names, faces,
   * the running tally — is stripped, so the arena is not drawn at all: two nameless silhouettes
   * with no bars is not a battle screen, and drawing one would only be an invitation to put the
   * names back.
   */
  blind: boolean;
  tally: Tally;
  /** Set only for a voter, and only once there is something to compare. Labelled, never named. */
  works: Array<{ label: BlindLabel; url: string }>;
  myVote: { label: BlindLabel; axis: Osa } | null;
  brief: { id: string; title: string; task: string | null; estMin: number; islandId: string } | null;
  faces: Record<string, { look: number; souvenirs: string[]; island: string | null }>;
}

export interface ChallengeOffer {
  refusal: string | null;
  name: string;
  briefs: Array<{
    id: string;
    title: string;
    task: string | null;
    estMin: number;
    islandId: string;
    /** This member has posted a work on it before; the duel is a redo for them. */
    redoMine: boolean;
    redoTheirs: boolean;
  }>;
  /** A duel already running between the two of them: open it instead of starting another. */
  existing: string | null;
}

export interface PosadaView {
  peers: Peer[];
  works: PeerWork[];
  visible: boolean;
  name: string;
  /** Duels open for voting. The crew screen is the only place in the app that offers one. */
  dvoboji: Duel[];
  dvobojGlasovi: Record<string, Tally>;
}

/**
 * An island somebody made, as the screen needs it.
 *
 * `kod` is present only on your own island: a guest who has been let in once must not walk away with
 * the key to let themselves back in after it has been shut.
 */
export interface DomView {
  svet: unknown;
  ime: [number | null, number | null];
  rev: number;
  mine: boolean;
  vrata?: DomDoor;
  kod?: string | null;
  /** Whether „vrati kako je bilo" has anything to go back to. Never a count, never a history. */
  vrati?: boolean;
  domacin?: { id: string; name: string; look: number };
}

export interface Bootstrap {
  contentVersion: number;
  mailProvider: 'cloudflare' | 'resend' | 'log' | 'none';
  serverTime: number;
  /** The active curriculum. Present only when the request carried a valid session. */
  content?: RawContent;
  state: ServerState | null;
}

export interface AdminDraft {
  data: RawContent;
  rev: number;
  baseVersion: number | null;
  updatedAt: number | null;
  updatedBy: string | null;
  dirty: boolean;
}

export interface AdminContentState {
  draft: AdminDraft;
  active: { version: number; revisionId: number };
}

export interface ImpactCount {
  id: string;
  members: number;
}

export interface ValidationReport {
  problems: ContentProblem[];
  diff: DiffLine[];
  impact: {
    removedNodesWithProgress: ImpactCount[];
    removedIslandsWithState: ImpactCount[];
    changedSubmissionWithWork: ImpactCount[];
  };
  canPublish: boolean;
  dirty: boolean;
  /** The version this publish would mint, decided by the server. */
  nextVersion: number;
  /** The draft revision the report describes. */
  rev: number;
}

export interface RevisionMeta {
  id: number;
  version: number;
  note: string | null;
  summary: string[];
  publishedAt: number;
  publishedBy: string | null;
  publishedByEmail: string | null;
  bytes: number;
  active: boolean;
}

export type QueuedAction = {
  clientId: string;
  nodeId: string;
  action: 'done' | 'post' | 'feedback' | 'revise';
  url?: string;
  at: number;
};

const QUEUE_KEY = 'talas.queue.v1';
const CACHE_KEY = 'talas.state.v1';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    public detail?: unknown,
  ) {
    super(code);
  }
}

/**
 * A request that cannot hang forever. On a phone that has drifted onto a captive-portal wifi, a
 * fetch can sit open indefinitely — and the one thing the app must never do is show a loading
 * screen with no way out.
 */
const TIMEOUT_MS = 15_000;

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  let response: Response;
  try {
    response = await fetch(path, {
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      signal: controller.signal,
      ...init,
    });
  } finally {
    clearTimeout(timer);
  }
  const text = await response.text();
  const body = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  if (!response.ok) throw new ApiError(response.status, String(body.error ?? response.status), body);
  return body as T;
}

export const api = {
  bootstrap: () => request<Bootstrap>('/api/bootstrap'),
  state: () => request<ServerState>('/api/state'),

  requestLogin: (email: string) =>
    request<{
      status: string;
      provider?: string;
      devLink?: string;
      devCode?: string;
      retryAfterMs?: number;
    }>('/api/auth/request', { method: 'POST', body: JSON.stringify({ email }) }),

  confirmLogin: (token: string) =>
    request<{ status: string } & Partial<ServerState>>('/api/auth/confirm', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  confirmCode: (email: string, code: string) =>
    request<{ status: string } & Partial<ServerState>>('/api/auth/code', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    }),

  logout: () => request<{ status: string }>('/api/auth/logout', { method: 'POST' }),

  onboard: (path: string, look: number, name?: string, ostrvo?: string | null) =>
    request<ServerState>('/api/onboarding', {
      method: 'POST',
      body: JSON.stringify({ path, look, name, ostrvo }),
    }),

  /**
   * A new appearance. `ostrvo` is where the map thinks this member is standing, so the Worker can
   * tell that room — without it the change is correct everywhere and invisible to the four people
   * who happen to be looking at it.
   */
  saveIzgled: (look: number, ostrvo: string | null) =>
    request<{ look: number }>('/api/izgled', {
      method: 'POST',
      body: JSON.stringify({ look, ostrvo }),
    }),

  /**
   * Where they were standing. Returns nothing worth reading and is never awaited by anything on
   * screen — a position that fails to save costs a member one restore, so it is the one write in
   * here that is allowed to be dropped on the floor.
   */
  saveMesto: (mesto: Mesto) =>
    request<{ ok: boolean }>('/api/mesto', { method: 'POST', body: JSON.stringify(mesto) }),

  /** MOJE OSTRVO. `id` reads somebody else's, if their door is open to us. */
  dom: {
    get: (id?: string) => request<DomView>(id ? `/api/dom/${encodeURIComponent(id)}` : '/api/dom'),
    beginEdit: () => request<{ ok: boolean }>('/api/dom/uredi', { method: 'POST' }),
    /**
     * Store the island. The response carries the island as it was actually kept, which can differ
     * from what was sent: a dock painted into the sea is relocated rather than refused.
     */
    save: (svet: unknown, rev: number) =>
      request<{ ok: boolean; rev: number; svet?: unknown }>('/api/dom', {
        method: 'PUT',
        body: JSON.stringify({ svet, rev }),
      }),
    revert: () => request<DomView & { ok: boolean }>('/api/dom/vrati', { method: 'POST' }),
    name: (a: number, b: number) =>
      request<{ ok: boolean }>('/api/dom/ime', { method: 'POST', body: JSON.stringify({ a, b }) }),
    door: (vrata: DomDoor) =>
      request<{ ok: boolean; vrata: DomDoor; kod: string | null }>('/api/dom/vrata', {
        method: 'POST',
        body: JSON.stringify({ vrata }),
      }),
    /** A code identifies the island — see `admitByCode` for why, and what it costs. */
    enter: (kod: string) =>
      request<{ ok: boolean; id: string }>('/api/dom/ulaz', {
        method: 'POST',
        body: JSON.stringify({ kod }),
      }),
    /** Walk through a door that is simply open. */
    knock: (id: string) =>
      request<{ ok: boolean; id: string }>(`/api/dom/${encodeURIComponent(id)}/ulaz`, {
        method: 'POST',
      }),
    open: () =>
      request<{ ostrva: Array<{ id: string; name: string; ime: [number | null, number | null]; look: number }> }>(
        '/api/dom/lista/otvorena',
      ),
  },

  progress: (action: QueuedAction) =>
    request<ServerState & { opened?: string[]; cleared?: string[]; duplicate?: boolean }>(
      '/api/progress',
      { method: 'POST', body: JSON.stringify(action) },
    ),

  progressBatch: (items: QueuedAction[]) =>
    request<ServerState & { results: Array<{ clientId?: string; ok: boolean; error?: string }> }>(
      '/api/progress/batch',
      { method: 'POST', body: JSON.stringify({ items }) },
    ),

  journal: () =>
    request<{
      entries: Array<{
        kind: string;
        nodeId: string | null;
        islandId: string | null;
        at: number;
        payload: Record<string, unknown>;
      }>;
    }>('/api/journal'),

  content: () => request<{ version: number; content: RawContent }>('/api/content'),

  /**
   * The duel. Person-to-person, so none of it is ever queued offline (see the note in `store.ts`):
   * a challenge replayed from a queue three days late lands on somebody who has moved on.
   */
  dvoboj: {
    one: (id: string) => request<DuelView>(`/api/dvoboj/${encodeURIComponent(id)}`),
    offer: (memberId: string) =>
      request<ChallengeOffer>(`/api/dvoboj/izazov/${encodeURIComponent(memberId)}`),
    challenge: (body: {
      memberId: string;
      nodeId: string;
      workHours: number;
      voteHours: number;
      islandId?: string;
    }) => request<DuelView & { ok: boolean }>('/api/dvoboj', { method: 'POST', body: JSON.stringify(body) }),
    answer: (
      id: string,
      body: { action: 'prihvati' | 'odbij' | 'izmena'; nodeId?: string; workHours?: number; voteHours?: number },
    ) =>
      request<DuelView & { ok: boolean }>(`/api/dvoboj/${encodeURIComponent(id)}/odgovor`, {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    submit: (id: string, url: string) =>
      request<ServerState & DuelView & { ok: boolean }>(
        `/api/dvoboj/${encodeURIComponent(id)}/predaja`,
        { method: 'POST', body: JSON.stringify({ url }) },
      ),
    vote: (id: string, label: BlindLabel, axis: Osa) =>
      request<DuelView & { ok: boolean }>(`/api/dvoboj/${encodeURIComponent(id)}/glas`, {
        method: 'POST',
        body: JSON.stringify({ label, axis }),
      }),
    revise: (id: string, url: string) =>
      request<ServerState & DuelView & { ok: boolean }>(
        `/api/dvoboj/${encodeURIComponent(id)}/popravka`,
        { method: 'POST', body: JSON.stringify({ url }) },
      ),
    forfeit: (id: string) =>
      request<DuelView & { ok: boolean }>(`/api/dvoboj/${encodeURIComponent(id)}/prekid`, {
        method: 'POST',
      }),
    seen: (id: string) =>
      request<{ ok: boolean }>(`/api/dvoboj/${encodeURIComponent(id)}/vidjeno`, { method: 'POST' }),
  },

  posada: {
    view: () => request<PosadaView>('/api/posada'),
    /** „Ostavio sam komentar" — the claim, aimed at one person who will go and look. */
    comment: (memberId: string, nodeId: string) =>
      request<ServerState & { ok: boolean }>('/api/posada/comment', {
        method: 'POST',
        body: JSON.stringify({ memberId, nodeId }),
      }),
    reply: (id: string, action: 'hvala' | 'nema') =>
      request<ServerState & { ok: boolean }>('/api/posada/reply', {
        method: 'POST',
        body: JSON.stringify({ id, action }),
      }),
    /** Marks notifications read, in whichever direction this member is on them. */
    seen: (ids: string[]) =>
      request<ServerState & { ok: boolean }>('/api/posada/reply', {
        method: 'POST',
        body: JSON.stringify({ action: 'videh', ids }),
      }),
    me: (patch: { name?: string; visible?: boolean }) =>
      request<ServerState & { ok: boolean; name: string }>('/api/posada/me', {
        method: 'POST',
        body: JSON.stringify(patch),
      }),
  },

  admin: {
    members: () => request<{ members: Array<Record<string, unknown>> }>('/api/admin/members'),
    addMembers: (text: string) =>
      request<{ added: number; updated: number; parsed: number }>('/api/admin/members', {
        method: 'POST',
        body: JSON.stringify({ text }),
      }),
    issueLink: (id: string) =>
      request<{ link: string; code: string; expiresAt: number; email: string }>(
        `/api/admin/members/${id}/link`,
        { method: 'POST' },
      ),
    setStatus: (id: string, status: 'active' | 'revoked') =>
      request<{ ok: boolean }>(`/api/admin/members/${id}/status`, {
        method: 'POST',
        body: JSON.stringify({ status }),
      }),
    setRole: (id: string, role: 'admin' | 'member') =>
      request<{ ok: boolean; role: string }>(`/api/admin/members/${id}/role`, {
        method: 'POST',
        body: JSON.stringify({ role }),
      }),
    queue: () =>
      request<{
        queue: Array<Record<string, unknown>>;
        claims: Array<Record<string, unknown>>;
      }>('/api/admin/queue'),
    markFeedback: (memberId: string, nodeId: string) =>
      request<{ ok: boolean }>('/api/admin/queue/feedback', {
        method: 'POST',
        body: JSON.stringify({ memberId, nodeId }),
      }),
    stats: () => request<Record<string, number>>('/api/admin/stats'),

    content: {
      get: () => request<AdminContentState>('/api/admin/content'),
      saveDraft: (data: RawContent, rev: number) =>
        request<{ ok: boolean; rev: number }>('/api/admin/content/draft', {
          method: 'PUT',
          body: JSON.stringify({ data, rev }),
        }),
      validate: () => request<ValidationReport>('/api/admin/content/validate', { method: 'POST' }),
      publish: (rev: number, note?: string) =>
        request<{ ok: boolean; version: number; summary: string[] }>('/api/admin/content/publish', {
          method: 'POST',
          body: JSON.stringify({ rev, note }),
        }),
      discard: () => request<AdminContentState>('/api/admin/content/discard', { method: 'POST' }),
      revisions: () => request<{ revisions: RevisionMeta[] }>('/api/admin/content/revisions'),
      revision: (id: number) =>
        request<{ meta: RevisionMeta; data: RawContent }>(`/api/admin/content/revisions/${id}`),
      restore: (id: number) =>
        request<AdminContentState & { ok: boolean }>(`/api/admin/content/revisions/${id}/restore`, {
          method: 'POST',
        }),
    },
  },
};

/* ------------------------------------------------------------------ local durability */

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private browsing, or a full quota. Losing the cache is survivable; crashing is not.
  }
}

export const localQueue = {
  all: (): QueuedAction[] => readJson<QueuedAction[]>(QUEUE_KEY, []),
  push(action: QueuedAction): void {
    const queue = localQueue.all();
    if (queue.some((item) => item.clientId === action.clientId)) return;
    queue.push(action);
    writeJson(QUEUE_KEY, queue.slice(-100));
  },
  remove(clientIds: string[]): void {
    const drop = new Set(clientIds);
    writeJson(
      QUEUE_KEY,
      localQueue.all().filter((item) => !drop.has(item.clientId)),
    );
  },
  clear(): void {
    writeJson(QUEUE_KEY, []);
  },
};

export const localState = {
  read: (): ServerState | null => readJson<ServerState | null>(CACHE_KEY, null),
  write: (state: ServerState | null) => writeJson(CACHE_KEY, state),
  /**
   * Drop the cached session but keep the queue. Signing out, or a session that lapsed while the
   * tab was closed, must not destroy work that has not reached the server yet.
   */
  forgetSession: () => {
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch {
      /* private mode */
    }
  },
  clear: () => {
    try {
      localStorage.removeItem(CACHE_KEY);
      localStorage.removeItem(QUEUE_KEY);
    } catch {
      /* nothing to do */
    }
  },
};

export function newClientId(): string {
  return crypto.randomUUID();
}
